import {useEffect, useState} from 'react';

import Spinner from '@/assets/svg/Spinner';
import {Tabs} from '@/components';
import SystemHealthBanner from '@/components/SystemHealthBanner';
import {processorService} from '@/services/processorService';
import type {TabItem} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';

import MetricsDashboard from './MetricDashboard';
import ProcessorMetricsGrouped from './MetricsGrouped';
import RawMetrics from './RawMetrics';
import type {Metrics, MetricsData} from './types';

const MetricPage = () => {
  const [data, setData] = useState<MetricsData[]>([]);
  const [metrics, setMetrics] = useState<Metrics | undefined>();

  useEffect(() => {
    const fetchData = async () => {
      const response = await processorService.getProcessorMetrics();
      setData(response.data);
      setMetrics(response.metrics);
    };

    // Unlike the other Processor screens, `fetchData` has no try/catch of its
    // own, so this handler is the only thing standing between a failed metrics
    // request and an unhandled rejection behind a spinner that never clears.
    // Surfaced rather than logged: without a toast the screen gives the admin
    // no way to tell "still loading" from "the request failed".
    fetchData().catch((e: unknown) =>
      showErrorMessage(
        `Fetching processor metrics failed: ${getErrorMessage(e, String(e))}`,
      ),
    );
  }, []);

  const tabs: TabItem[] = [
    {
      id: 'metrics-overview',
      title: 'Overview',
      content: <MetricsDashboard data={data} metrics={metrics} />,
    },
    {
      id: 'metric-groups',
      title: 'Metric Groups',
      content: <ProcessorMetricsGrouped data={metrics} />,
    },
    {
      id: 'metric-raw',
      title: 'Raw Data',
      content: <RawMetrics data={data} />,
    },
  ];

  // Shown while loading too: a slow or stuck load here is exactly when someone
  // would want to know how the partners are behaving.
  const banner = (
    <SystemHealthBanner reason="These metrics summarise completed transactions over time. For what is happening right now, including partners failing calls, payouts refunded or stuck by rail, and alerts currently firing, use the system health dashboard." />
  );

  if (!data || !metrics) {
    return (
      <>
        {banner}
        <div className="h-56 flex items-center justify-center">
          <Spinner size={8} className="text-cyan-600" />
        </div>
      </>
    );
  }

  return (
    <>
      {banner}
      <Tabs tabs={tabs} queryKey={'tab'} />
    </>
  );
};

export default MetricPage;
